// Statement period reconciliation (PD-16379).
//
// Pure, platform-agnostic logic that turns a client's invoices, payments and
// account-credit ledger into the statement's "activity" block: the balance
// entering the period, the invoices and payments inside it, and the balance
// leaving it.
//
// Why it is built this way:
// - Every balance is derived from the STORED invoice balances — the figures
//   the invoice screens show, and for ERP-mastered tenants the figures the
//   ERP integrates. The balance on an earlier day is the stored balance with
//   every payment dated after that day added back, one transaction at a time
//   (`calculateInvoicePaymentApplications`, the same netting the stored
//   balance was computed with). The previous monthly ledger re-derived the
//   balance from payment rows instead, so any payment that was not applied
//   to an invoice, or that the ERP recorded without Workd seeing it, pulled
//   the ledger away from what the customer owes.
// - A payment's application to an invoice takes effect on the later of the
//   payment date and the invoice date: money received before an invoice
//   exists (a prepaid order) pays it down when it is issued.
// - Whatever the listed payments cannot explain on an invoice issued in the
//   period is itemized as an adjustment on that invoice, never lumped.
// - With those three rules, closing = opening + invoices − payments +
//   adjustments holds exactly, and closing equals the total balance due.
// - When the client's previous stored statement ends the day before this
//   period starts, the opening balance is THAT statement's closing balance —
//   what the customer was last told — and every invoice whose balance at the
//   start of this period differs from what that statement showed is
//   itemized as a "since last statement" adjustment (an ERP-side write-off,
//   a cancellation, a backdated invoice). Those rows sum to the difference
//   between the carried opening and the rolled-back one, so the identity and
//   the closing balance are unchanged. Without such a statement the opening
//   is the rolled-back balance.
// - Money on the customer's account that has not been applied to an invoice
//   (deposits, on-account ERP payments) is listed and reported as available
//   credit but is NOT subtracted from the balance due until it is applied.
// - PD-16674 — open credit memos follow the same rule: until a memo is
//   applied to an invoice its amount is still owed on that invoice, so the
//   memo is left out of every balance (opening, invoices & charges, payments
//   & credits, adjustments, closing) and reported as available credit
//   instead. Issuing a memo therefore moves nothing; APPLYING it moves the
//   invoice it was applied to, which shows up as a payment & credit on that
//   invoice (a Workd application) or as an adjustment on it (an application
//   made in the ERP). That keeps the identity below exact with no new line,
//   and it is the same thing that happens to account funds. A memo refunded
//   or moved to account credit leaves the available credit and never touched
//   the balance due.
// - PD-17190 — a tenant whose ERP nets open credit memos into its own
//   statement balance (HF Foods' Workday) turns on
//   `statements.credit_memos_reduce_balance`, and its statements keep the
//   pre-PD-16674 rule: a memo is counted like any other document, so its
//   open credit lowers the opening, closing and invoices & charges figures
//   and `availableCreditMemos` is left unset.
// - All arithmetic is in integer cents so sums cannot drift by a cent.

import type {
	CustomerStatementModel,
	StatementActivityAdjustmentModel,
	StatementActivityApplicationModel,
	StatementActivityInvoiceModel,
	StatementActivityModel,
	StatementActivityOpeningSourceModel,
	StatementActivityPaymentKind,
	StatementActivityPaymentModel,
	StatementSourceAccountLedgerModel,
	StatementSourceInvoiceModel,
	StatementSourceModel,
	StatementSourcePaymentModel,
	StatementSourcePreviousStatementModel,
} from '@supply-mover-2-0/shared';
import {
	InvoiceStatus,
	InvoiceType,
	statementRecordNumber,
} from '@supply-mover-2-0/shared';

/** The `customer_funds_ledger` type a spend row carries. */
const LEDGER_SPEND_TYPE = 'spend';

/** The `customer_funds_ledger` type the ERP payment import writes. */
const LEDGER_ERP_SYNC_TYPE = 'erp_sync';

const ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})$/;

/** Year, month (1-12) and day of a 'YYYY-MM-DD' key already matched by ISO_DATE. */
function splitKey(key: string): { year: number; month: number; day: number } {
	const match = ISO_DATE.exec(key);
	return {
		year: Number(match?.[1] ?? 0),
		month: Number(match?.[2] ?? 0),
		day: Number(match?.[3] ?? 0),
	};
}

/** 'YYYY-MM-DD' from an ISO date or datetime, or null when unparseable. */
export function statementDateKey(
	iso: string | null | undefined,
): string | null {
	if (iso == null || iso.length < 10) {
		return null;
	}
	const key = iso.slice(0, 10);
	if (!ISO_DATE.test(key)) {
		return null;
	}
	const { year, month, day } = splitKey(key);
	const check = new Date(Date.UTC(year, month - 1, day));
	// Rejects impossible dates (Feb 31) instead of letting Date roll them over.
	if (
		check.getUTCFullYear() !== year ||
		check.getUTCMonth() !== month - 1 ||
		check.getUTCDate() !== day
	) {
		return null;
	}
	return key;
}

/** The calendar day after a 'YYYY-MM-DD' key. */
function nextDay(key: string): string {
	const { year, month, day } = splitKey(key);
	return new Date(Date.UTC(year, month - 1, day + 1))
		.toISOString()
		.slice(0, 10);
}

/** The calendar day before a 'YYYY-MM-DD' key. */
function previousDay(key: string): string {
	const { year, month, day } = splitKey(key);
	return new Date(Date.UTC(year, month - 1, day - 1))
		.toISOString()
		.slice(0, 10);
}

/**
 * Dollars to integer cents, half away from zero. The value is first fixed to
 * six places so a float such as 1.005 (stored as 1.00499…) rounds as written.
 */
export function toCents(value: number | null | undefined): number {
	if (value == null || !Number.isFinite(value)) {
		return 0;
	}
	const cents = Math.round(Number((Math.abs(value) * 100).toFixed(6)));
	if (cents === 0) {
		return 0;
	}
	return value < 0 ? -cents : cents;
}

export function fromCents(cents: number): number {
	return cents / 100;
}

export function isCancelledStatementInvoice(
	invoice: Pick<StatementSourceInvoiceModel, 'status'>,
): boolean {
	return invoice.status === InvoiceStatus.CANCELLED;
}

export function isCreditMemoStatementInvoice(
	invoice: Pick<StatementSourceInvoiceModel, 'type' | 'total'>,
): boolean {
	return invoice.type === InvoiceType.CREDIT_MEMO || invoice.total < 0;
}

/**
 * A document's stored open balance in cents, signed the way the statement
 * sums it: what the customer owes is positive, credit owed back to them is
 * negative. PD-15441 — ERP-synced credit memos store their remaining credit
 * POSITIVE while Workd-issued ones store it negative; both mean credit owed
 * to the customer, so a memo's balance is read by magnitude.
 */
function storedBalanceCents(invoice: StatementSourceInvoiceModel): number {
	const cents = toCents(invoice.outstandingBalance);
	return isCreditMemoStatementInvoice(invoice) ? -Math.abs(cents) : cents;
}

interface InvoiceEntry {
	invoice: StatementSourceInvoiceModel;
	date: string;
	storedCents: number;
	totalCents: number;
	/** Carried by this account and not cancelled. */
	counted: boolean;
	/**
	 * A credit memo held as available credit, never part of the balance due.
	 * False for every document when the tenant nets memos into the balance
	 * (PD-17190); `isCreditMemo` still says what the document is.
	 */
	isCredit: boolean;
}

/** Part of the balance due: a counted document that is not a credit memo. */
function isOwed(entry: InvoiceEntry | undefined): boolean {
	return entry != null && entry.counted && !entry.isCredit;
}

interface Contribution {
	payment: StatementSourcePaymentModel;
	paymentDate: string;
	invoiceId: number;
	cents: number;
	/** The later of the payment date and the invoice date. */
	effectiveDate: string;
}

interface WorkingLine {
	line: StatementActivityPaymentModel;
	parentId: number | null;
	/** Per-invoice applied cents, for pairing a void with what it reversed. */
	appliedByInvoice: Map<number, number>;
	appliedCents: number;
	depositedCents: number;
}

export interface StatementActivityWindowModel {
	/** 'YYYY-MM-DD' first day of the period, or null for all history. */
	periodStart: string | null;
	/** 'YYYY-MM-DD' last day of the period (inclusive). */
	periodEnd: string;
}

export interface StatementActivityResultModel {
	activity: StatementActivityModel;
	/**
	 * Balance at the end of the period, in cents, for every counted invoice
	 * (credit memos excluded) dated on or before the period end — the aging
	 * and open items read these so the total due is the closing balance to the
	 * cent.
	 */
	balancesAtEndCents: Map<number, number>;
	/**
	 * PD-16674 — credit still open at the end of the period, in positive
	 * cents, for every counted credit memo dated on or before the period end
	 * that has any. Sums to `activity.availableCreditMemos`.
	 */
	creditsAtEndCents: Map<number, number>;
}

function paymentKind(
	payment: StatementSourcePaymentModel,
): StatementActivityPaymentKind {
	return payment.kind ?? 'PAYMENT';
}

/**
 * PD-16379 — what a stored statement said at its end, read from its
 * snapshot: its closing balance and the balance of every document it showed
 * as open. `invoiceBalances` is null for a snapshot stored before statements
 * listed open items (PD-15854). Works for both snapshot generations: the open
 * items of a statement built before the activity block are the balances as
 * stored when it was generated, and they sum to its total due just as the
 * newer ones do.
 */
export function previousStatementFromSnapshot(
	statementId: number,
	snapshot: Pick<
		CustomerStatementModel,
		'clientId' | 'statementDate' | 'periodEnd' | 'totalBalance' | 'openItems'
	> & { activity?: Pick<StatementActivityModel, 'availableCreditMemos'> },
): StatementSourcePreviousStatementModel | null {
	const statementDate = statementDateKey(snapshot.statementDate);
	if (statementDate == null) {
		return null;
	}
	// The same window end the engine used when it built that statement.
	const explicitEnd = statementDateKey(snapshot.periodEnd);
	const periodEnd =
		explicitEnd != null && explicitEnd < statementDate
			? explicitEnd
			: statementDate;

	return {
		statementId,
		number: statementRecordNumber(statementId),
		clientId: snapshot.clientId,
		periodEnd,
		closingBalance: snapshot.totalBalance,
		creditMemosHeldAsCredit: snapshot.activity?.availableCreditMemos != null,
		invoiceBalances:
			snapshot.openItems != null
				? snapshot.openItems.map((item) => ({
						invoiceId: item.invoiceId,
						number: item.number,
						date: statementDateKey(item.date),
						balance: item.outstanding,
						// Credit memos are the only documents issued for a negative
						// total; only snapshots stored before PD-16674 list them.
						isCreditMemo: item.originalTotal < 0,
					}))
				: null,
	};
}

interface PriorBalances {
	opening: StatementActivityOpeningSourceModel;
	closingCents: number;
	byInvoice: Map<
		number,
		{ number: string; date: string | null; cents: number }
	>;
}

/**
 * The previous statement's per-invoice balances, when this statement may
 * open from it: same client, its last day is exactly the day before this
 * period starts (a gap or an overlap would leave days counted twice or not
 * at all), it lists its open documents, and they add up to its closing
 * balance to the cent (otherwise the itemized differences could not
 * reconcile to it). Null means "open from the rolled-back balance".
 *
 * PD-16674 — and it lists no credit memo. A statement that does was stored
 * before open credit memos stopped reducing the balance due, so its closing
 * balance is net of them. Carrying it would either open from a figure this
 * statement no longer means, or itemize each still-open memo as a "balance
 * change since last statement" when nothing about it changed. Such a
 * statement opens from the rolled-back balance instead, once; the next one
 * carries normally.
 *
 * PD-17190 — when this statement nets the memos instead, the rule turns
 * around: a previous statement that held them as available credit is the
 * one that cannot be carried, for the same reason.
 */
function usablePriorBalances(
	previous: StatementSourcePreviousStatementModel | null | undefined,
	clientId: number,
	start: string | null,
	isCreditMemoId: (invoiceId: number) => boolean,
	creditMemosReduceBalance: boolean,
): PriorBalances | null {
	if (
		previous == null ||
		start == null ||
		previous.clientId !== clientId ||
		previous.invoiceBalances == null ||
		previous.periodEnd !== previousDay(start)
	) {
		return null;
	}
	const sameMemoRule = creditMemosReduceBalance
		? previous.creditMemosHeldAsCredit !== true
		: !previous.invoiceBalances.some(
				(item) => item.isCreditMemo === true || isCreditMemoId(item.invoiceId),
			);
	if (!sameMemoRule) {
		return null;
	}

	const byInvoice = new Map<
		number,
		{ number: string; date: string | null; cents: number }
	>();
	let sumCents = 0;
	for (const item of previous.invoiceBalances) {
		const cents = toCents(item.balance);
		sumCents += cents;
		const existing = byInvoice.get(item.invoiceId);
		byInvoice.set(item.invoiceId, {
			number: item.number,
			date: item.date,
			cents: (existing?.cents ?? 0) + cents,
		});
	}

	const closingCents = toCents(previous.closingBalance);
	if (sumCents !== closingCents) {
		return null;
	}

	return {
		opening: {
			statementId: previous.statementId,
			number: previous.number,
			periodEnd: previous.periodEnd,
		},
		closingCents,
		byInvoice,
	};
}

/**
 * Build the reconciled activity block for one statement window. Pure and
 * deterministic; `source` carries full history.
 */
export function buildStatementActivity(
	source: StatementSourceModel,
	window: StatementActivityWindowModel,
): StatementActivityResultModel {
	const end = window.periodEnd;
	// A start after the end would leave the opening and closing sets
	// describing different invoices; treat it as an empty period instead.
	const start =
		window.periodStart != null && window.periodStart > end
			? nextDay(end)
			: window.periodStart;
	const isBeforePeriod = (date: string): boolean =>
		start != null && date < start;
	const isInPeriod = (date: string): boolean =>
		date <= end && (start == null || date >= start);
	const creditMemosReduceBalance = source.creditMemosReduceBalance === true;

	// 1) Documents.
	const invoices = new Map<number, InvoiceEntry>();
	for (const invoice of source.invoices) {
		const date = statementDateKey(invoice.date);
		if (date == null) {
			continue;
		}
		invoices.set(invoice.id, {
			invoice,
			date,
			storedCents: storedBalanceCents(invoice),
			totalCents: toCents(invoice.total),
			counted:
				!isCancelledStatementInvoice(invoice) && invoice.billedToName == null,
			isCredit:
				!creditMemosReduceBalance && isCreditMemoStatementInvoice(invoice),
		});
	}

	// 2) Every payment's effect on every counted document, dated. Credit
	//    memos are included so their open credit can be rolled back too.
	const contributions: Array<Contribution> = [];
	for (const payment of source.payments) {
		const paymentDate = statementDateKey(payment.date);
		if (paymentDate == null) {
			continue;
		}
		for (const application of payment.applications ?? []) {
			const entry = invoices.get(application.invoiceId);
			const cents = toCents(application.amount);
			if (entry == null || !entry.counted || cents === 0) {
				continue;
			}
			contributions.push({
				payment,
				paymentDate,
				invoiceId: application.invoiceId,
				cents,
				effectiveDate: paymentDate > entry.date ? paymentDate : entry.date,
			});
		}
	}

	// 3) Balances rolled back from the stored figures.
	const paidAfterEnd = new Map<number, number>();
	const paidFromStart = new Map<number, number>();
	const paidInPeriod = new Map<number, number>();
	const add = (map: Map<number, number>, id: number, cents: number): void => {
		map.set(id, (map.get(id) ?? 0) + cents);
	};
	for (const c of contributions) {
		if (c.effectiveDate > end) {
			add(paidAfterEnd, c.invoiceId, c.cents);
		}
		if (start != null && c.effectiveDate >= start) {
			add(paidFromStart, c.invoiceId, c.cents);
		}
		if (isInPeriod(c.effectiveDate)) {
			add(paidInPeriod, c.invoiceId, c.cents);
		}
	}

	const balancesAtEndCents = new Map<number, number>();
	const creditsAtEndCents = new Map<number, number>();
	let availableCreditMemosCents = 0;
	let openingCents = 0;
	let closingCents = 0;
	let invoicedCents = 0;
	let paymentsCents = 0;
	const balancesAtStartCents = new Map<number, number>();
	const inPeriodAdjustments: Array<StatementActivityAdjustmentModel> = [];
	const activityInvoices: Array<StatementActivityInvoiceModel> = [];

	const sortedInvoices = Array.from(invoices.values()).sort((a, b) =>
		a.date !== b.date
			? a.date < b.date
				? -1
				: 1
			: a.invoice.id - b.invoice.id,
	);

	for (const entry of sortedInvoices) {
		const id = entry.invoice.id;
		if (entry.date > end) {
			continue;
		}

		const inPeriod = isInPeriod(entry.date);
		const endCents = entry.counted
			? entry.storedCents + (paidAfterEnd.get(id) ?? 0)
			: 0;

		if (entry.counted && entry.isCredit) {
			// PD-16674 — available credit, reported beside the balance due and
			// never in it. A memo whose end balance is not a credit (fully used,
			// or applied past its amount) has nothing available.
			const availableCents = -endCents;
			if (availableCents > 0) {
				creditsAtEndCents.set(id, availableCents);
				availableCreditMemosCents += availableCents;
			}
		} else if (entry.counted) {
			balancesAtEndCents.set(id, endCents);
			closingCents += endCents;
			paymentsCents += paidInPeriod.get(id) ?? 0;

			if (isBeforePeriod(entry.date)) {
				const startCents = entry.storedCents + (paidFromStart.get(id) ?? 0);
				openingCents += startCents;
				balancesAtStartCents.set(id, startCents);
			} else {
				invoicedCents += entry.totalCents;
				// What the listed payments leave unexplained on a document issued
				// in the period — an ERP-side adjustment, a write-off, a payment
				// recorded outside Workd.
				const expectedCents = entry.totalCents - (paidInPeriod.get(id) ?? 0);
				const adjustment = endCents - expectedCents;
				if (adjustment !== 0) {
					inPeriodAdjustments.push({
						kind: 'IN_PERIOD',
						invoiceId: id,
						number: entry.invoice.number,
						date: entry.date,
						balanceBefore: fromCents(expectedCents),
						balanceAfter: fromCents(endCents),
						amount: fromCents(adjustment),
					});
				}
			}
		}

		// Cancelled documents are left off the statement entirely, matching
		// cycle billing; documents billed to another account are listed for
		// reference with nothing owed here. A credit memo issued in the period
		// is listed with the credit still open on it, but is not part of
		// Invoices & charges (PD-16674).
		if (inPeriod && !isCancelledStatementInvoice(entry.invoice)) {
			activityInvoices.push({
				invoiceId: id,
				number: entry.invoice.number,
				isCreditMemo: isCreditMemoStatementInvoice(entry.invoice),
				date: entry.date,
				dueDate: statementDateKey(entry.invoice.dueDate),
				originalAmount: fromCents(entry.totalCents),
				balanceAtPeriodEnd: fromCents(endCents),
				lineItems: entry.invoice.lineItems,
				billedForName: entry.invoice.billedForName ?? null,
				billedToName: entry.invoice.billedToName ?? null,
			});
		}
	}

	// 4) Opening: carried from the previous statement when it ends the day
	//    before this period, with every invoice balance that changed since
	//    it itemized; otherwise the rolled-back balance.
	const prior = usablePriorBalances(
		source.previousStatement,
		source.clientId,
		start,
		(invoiceId) => {
			const entry = invoices.get(invoiceId);
			return entry != null && isCreditMemoStatementInvoice(entry.invoice);
		},
		creditMemosReduceBalance,
	);
	const sinceLastAdjustments: Array<StatementActivityAdjustmentModel> = [];
	let reportedOpeningCents = openingCents;
	if (prior != null) {
		reportedOpeningCents = prior.closingCents;
		const invoiceIds = new Set<number>([
			...prior.byInvoice.keys(),
			...balancesAtStartCents.keys(),
		]);
		for (const id of invoiceIds) {
			const before = prior.byInvoice.get(id);
			const beforeCents = before?.cents ?? 0;
			// Zero for a document that is no longer part of the balance here:
			// cancelled, deleted, billed to another account, or re-dated into
			// this period (its total is then counted with this period's
			// invoices).
			const afterCents = balancesAtStartCents.get(id) ?? 0;
			if (afterCents === beforeCents) {
				continue;
			}
			const current = invoices.get(id);
			sinceLastAdjustments.push({
				kind: 'SINCE_LAST_STATEMENT',
				invoiceId: id,
				number: current?.invoice.number ?? before?.number ?? String(id),
				date: current?.date ?? before?.date ?? null,
				balanceBefore: fromCents(beforeCents),
				balanceAfter: fromCents(afterCents),
				amount: fromCents(afterCents - beforeCents),
			});
		}
		sinceLastAdjustments.sort((a, b) => {
			const aDate = a.date ?? '';
			const bDate = b.date ?? '';
			return aDate !== bDate
				? aDate < bDate
					? -1
					: 1
				: a.invoiceId - b.invoiceId;
		});
	}
	const adjustmentItems = [...sinceLastAdjustments, ...inPeriodAdjustments];
	let adjustmentsCents = 0;
	for (const item of adjustmentItems) {
		adjustmentsCents += toCents(item.amount);
	}

	// 5) Payment lines.
	//
	// PD-17190 — a ledger row is dated when it was written, which is right for
	// money Workd itself puts on or takes off the account. An `erp_sync` row
	// that ADDS credit is different: it mirrors an ERP payment that was left on
	// account when the ERP received it, so its date is the payment's date. A
	// first import or a backfill writes those rows long after the payment
	// (HF's were written on 2026-10-13 for payments from 2024 and 2025), and
	// dated by the write they made old payments read as this period's
	// activity and pulled the statement's opening row back to their dates.
	const paymentDates = new Map<number, string>();
	for (const payment of source.payments) {
		const date = statementDateKey(payment.date);
		if (date != null) {
			paymentDates.set(payment.id, date);
		}
	}
	const ledgerDate = (row: StatementSourceAccountLedgerModel): string | null => {
		const written = statementDateKey(row.date);
		if (
			written == null ||
			row.type !== LEDGER_ERP_SYNC_TYPE ||
			row.amount <= 0 ||
			row.transactionId == null
		) {
			return written;
		}
		const paid = paymentDates.get(row.transactionId);
		return paid != null && paid < written ? paid : written;
	};
	const ledger = (source.accountLedger ?? [])
		.map((row) => ({ row, date: ledgerDate(row) }))
		.filter(
			(
				item,
			): item is { row: StatementSourceAccountLedgerModel; date: string } =>
				item.date != null,
		);
	const ledgerInPeriodByTransaction = new Map<
		number,
		Array<StatementSourceAccountLedgerModel>
	>();
	for (const { row, date } of ledger) {
		if (!isInPeriod(date) || row.transactionId == null) {
			continue;
		}
		const list = ledgerInPeriodByTransaction.get(row.transactionId) ?? [];
		list.push(row);
		ledgerInPeriodByTransaction.set(row.transactionId, list);
	}

	const contributionsByPayment = new Map<number, Array<Contribution>>();
	for (const c of contributions) {
		if (!isInPeriod(c.effectiveDate)) {
			continue;
		}
		const list = contributionsByPayment.get(c.payment.id) ?? [];
		list.push(c);
		contributionsByPayment.set(c.payment.id, list);
	}

	const working: Array<WorkingLine> = [];
	const attachedLedgerIds = new Set<number>();
	for (const payment of source.payments) {
		const date = statementDateKey(payment.date);
		if (date == null) {
			continue;
		}
		const applied = contributionsByPayment.get(payment.id) ?? [];
		const ledgerRows = ledgerInPeriodByTransaction.get(payment.id) ?? [];
		const listed =
			applied.length > 0 ||
			ledgerRows.length > 0 ||
			(payment.moneyMoved === true && isInPeriod(date));
		if (!listed) {
			continue;
		}

		const appliedByInvoice = new Map<number, number>();
		for (const c of applied) {
			add(appliedByInvoice, c.invoiceId, c.cents);
		}
		const applications: Array<StatementActivityApplicationModel> = [];
		let appliedCents = 0;
		for (const [invoiceId, cents] of appliedByInvoice) {
			if (cents === 0) {
				continue;
			}
			// PD-16674 — a credit memo's side of an application is listed so
			// the line shows where the credit went, but only the invoice side
			// is a payment & credit: the memo was never in the balance due.
			if (isOwed(invoices.get(invoiceId))) {
				appliedCents += cents;
			}
			applications.push({
				invoiceId,
				number: invoices.get(invoiceId)?.invoice.number ?? String(invoiceId),
				amount: fromCents(cents),
			});
		}

		// Spend rows are the mechanics of an account-credit payment (its
		// applications already show where the credit went); every other row
		// linked to the payment is money it put on, or took off, the account.
		let depositedCents = 0;
		for (const row of ledgerRows) {
			attachedLedgerIds.add(row.id);
			if (row.type !== LEDGER_SPEND_TYPE) {
				depositedCents += toCents(row.amount);
			}
		}

		working.push({
			parentId: payment.parentId ?? null,
			appliedByInvoice,
			appliedCents,
			depositedCents,
			line: {
				sourceId: payment.id,
				kind: paymentKind(payment),
				date,
				method: payment.method,
				reference: payment.reference,
				amount: fromCents(toCents(payment.amount)),
				fee: fromCents(toCents(payment.fee ?? 0)),
				applications,
				appliedTotal: fromCents(appliedCents),
				depositedToAccount: fromCents(depositedCents),
				accountCreditType: null,
			},
		});
	}

	// A charge and the void that reversed it inside the same period moved no
	// money; listing both would only add two lines that cancel out.
	const byId = new Map<number, WorkingLine>();
	for (const item of working) {
		byId.set(item.line.sourceId, item);
	}
	const dropped = new Set<number>();
	for (const item of working) {
		if (item.line.kind !== 'VOID' || item.parentId == null) {
			continue;
		}
		const parent = byId.get(item.parentId);
		if (
			parent == null ||
			dropped.has(parent.line.sourceId) ||
			item.depositedCents !== 0 ||
			parent.depositedCents !== 0
		) {
			continue;
		}
		const invoiceIds = new Set([
			...item.appliedByInvoice.keys(),
			...parent.appliedByInvoice.keys(),
		]);
		let cancels = true;
		for (const invoiceId of invoiceIds) {
			if (
				(item.appliedByInvoice.get(invoiceId) ?? 0) +
					(parent.appliedByInvoice.get(invoiceId) ?? 0) !==
				0
			) {
				cancels = false;
				break;
			}
		}
		if (cancels) {
			dropped.add(item.line.sourceId);
			dropped.add(parent.line.sourceId);
		}
	}

	const lines: Array<StatementActivityPaymentModel> = working
		.filter((item) => !dropped.has(item.line.sourceId))
		.map((item) => item.line);

	// Account-credit movements not tied to a listed payment (a credit memo
	// moved to account credit, a correction, an ERP on-account balance for a
	// payment dated in an earlier period).
	for (const { row, date } of ledger) {
		if (!isInPeriod(date) || attachedLedgerIds.has(row.id)) {
			continue;
		}
		const cents = toCents(row.amount);
		if (cents === 0) {
			continue;
		}
		lines.push({
			sourceId: row.id,
			kind: 'ACCOUNT_CREDIT',
			date,
			method: null,
			reference: null,
			amount: fromCents(cents),
			fee: 0,
			applications: [],
			appliedTotal: 0,
			depositedToAccount: row.type === LEDGER_SPEND_TYPE ? 0 : fromCents(cents),
			accountCreditType: row.type,
		});
	}

	lines.sort((a, b) => {
		if (a.date !== b.date) {
			return a.date < b.date ? -1 : 1;
		}
		if (a.kind !== b.kind) {
			// Ledger-only lines after the payments they may follow on the same day.
			return a.kind === 'ACCOUNT_CREDIT'
				? 1
				: b.kind === 'ACCOUNT_CREDIT'
					? -1
					: 0;
		}
		return a.sourceId - b.sourceId;
	});

	let accountCreditCents = 0;
	for (const { row, date } of ledger) {
		if (date <= end) {
			accountCreditCents += toCents(row.amount);
		}
	}

	return {
		activity: {
			openingBalance: fromCents(reportedOpeningCents),
			openingFrom: prior?.opening ?? null,
			invoicesAndCharges: fromCents(invoicedCents),
			paymentsAndCredits: fromCents(paymentsCents),
			adjustments: fromCents(adjustmentsCents),
			closingBalance: fromCents(closingCents),
			availableAccountCredit: fromCents(accountCreditCents),
			// Unset when the memos are netted into the balance (PD-17190), the
			// signal every view of the statement reads.
			...(creditMemosReduceBalance
				? {}
				: { availableCreditMemos: fromCents(availableCreditMemosCents) }),
			invoices: activityInvoices,
			payments: lines,
			adjustmentItems,
		},
		balancesAtEndCents,
		creditsAtEndCents,
	};
}
